import * as React from "react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Container from "@mui/material/Container";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import { useNavigate } from "react-router-dom";
import {
  GridActionsCellItem,
  GridToolbarFilterButton,
  GridPaginationModel,
  GridFilterModel,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
  GridFilterOperator,
  getGridStringOperators,
  GridLogicOperator,
} from "@mui/x-data-grid-pro";
import { DataGrid } from "../../util/DataGrid";
import {
  InvestmentMasterModel,
  LazyLoadEventModel,
} from "../../../all_Interfaces";
import { useEffect, useState } from "react";
import { DashboardService } from "../../../services/DashboardService";
import { alignProperty } from "@mui/material/styles/cssUtils";
import { useResizeDetector } from "react-resize-detector";

const InvestmentsDashboard = () => {
  const dashboardService = new DashboardService();
  const navigate = useNavigate();
  const [value, setValue] = useState("1");
  const [investments, setInvestments] = useState<InvestmentMasterModel[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: pageSize,
  });
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>({
    items: [],
    logicOperator:GridLogicOperator.And
  });
  const { width,ref } = useResizeDetector();
  const [columnWidths,setColumnWidths] = useState<any>({});

  useEffect(()=>{
    if(width){
      const newColumnWidths = {
        column1:width * 0.1,
        column2:width * 0.1
      };
      setColumnWidths(newColumnWidths);
    }
  },[width]);
  const formatNumber = (params:any) => {
    if(params.value !== null)
      return<div style={{textAlign:'right',width:'85%',paddingRight:'5px'}}>
        {params.value.toLocaleString('en-US')}
      </div>
     ;
  }

  const fetchData = async (
    paginationModel: GridPaginationModel,
    filterModel: GridFilterModel
  ) => {
    setLoading(true);
    var payload: LazyLoadEventModel = {
      filters: filterModel.items.map((d) => {
        return {
          value: d?.value != null ? d?.value : "",
          matchMode: "contains",
          identity: d?.field,
        };
      }),
      first: paginationModel.pageSize * paginationModel.page,
      globalFilter: null,

      rows: paginationModel.pageSize,
      sortOrder: 1,
      filterConditionalOperator: filterModel.logicOperator?.toString() != undefined ? filterModel.logicOperator?.toString(): "or",
    };

    if(payload.filters.length == 0){
      payload = {...payload,filterConditionalOperator:GridLogicOperator.And}
    }
    await dashboardService
      .getAllInvestments(payload)
      .then((data: any) => {
        console.log(data);
        const inv = data.result.investments.map(
          (d: InvestmentMasterModel, i: any) => {
            return {
              ...d,
              id: d.investmentId,
              assetManagerName:
                d.assetManager != null
                  ? d.assetManager.firstName + " " + d.assetManager.lastName
                  : "",
            };
          }
        );
        setInvestments(inv);
        setRowCount(data.result.rowCount);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData(paginationModel, filterModel);
  }, [paginationModel]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const columns: GridColDef[] = [
    {
      field: "action",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      disableExport: true,
      renderCell: (cellValues) => {
        const onEditClick = (e: any) => {
          e.stopPropagation(); // don't select this row after clicking
          navigate("/investmentmaster/" + "edit/" + cellValues.id);
          return console.log("EDIT  >>>>", cellValues.id);
        };

        const onViewClick = (e: any) => {
          e.stopPropagation(); // don't select this row after clicking
          navigate("/investmentmaster/" + "view/" + cellValues.id);
          return console.log("View  >>>>", cellValues);
        };
        return (
          <>
            <GridActionsCellItem
              sx={{
                color: "#253746",
              }}
              icon={<VisibilityIcon />}
              label="View"
              onClick={(event) => {
                onViewClick(event);
              }}
            />
            <GridActionsCellItem
              sx={{
                color: "#253746",
              }}
              icon={<EditIcon />}
              label="Edit"
              onClick={(event) => {
                onEditClick(event);
              }}
            />
          </>
        );
      },
      width:columnWidths.column1 || 100,
    },
    {
      field: "investmentId",
      headerName: "Investment ID",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
      
    },
    {
      field: "name",
      headerName: "Investment Name",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "fund",
      headerName: "Fund",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "assetManagerName",
      headerName: "Asset Manager",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "investmentType",
      headerName: "Investment Type",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "propertyType",
      headerName: "Property Type",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "city",
      headerName: "City",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "state",
      headerName: "State",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "country",
      headerName: "Country",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    }, {
      field: "hasDebtLiability",
      headerName: "Has Debt Liability",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),
    },
    {
      field: "lastAppraisedValue",
      headerName: "Last Appraised Value",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),renderCell:formatNumber
    },
    {
      field: "lastAnnualAppraisedValue",
      headerName: "Last Annual Appraised Value",
      width:columnWidths.column2 || 100,
      filterOperators: getGridStringOperators().filter(
        (operator) => operator.value === "contains"
      ),renderCell:formatNumber
    },
  ];

  const handelPaginationModel = (e: GridPaginationModel) => {
    console.log(e);
    setPaginationModel(e);
    fetchData(e, filterModel);
  };
  const handelFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    // const count = model?.items.length != 0 ? Object.keys(model?.items[0]).length-1 : 0;
    // if (count === 4) {
      fetchData(paginationModel, model);
    // }
    console.log(model);
  };
  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarExport />
        <GridToolbarFilterButton />
      </GridToolbarContainer>
    );
  };
  return (
    <Container maxWidth={false} className="dashboard-wrap">
      <Box ref={ref} sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Button sx={{textTransform:"none"}}
              className="head-action-btn text-right"
              variant="contained"
              color="success"
              onClick={() => navigate("/investmentmaster/new")}
            >
              <AddIcon />
              New Investment
            </Button>
            <TabList
              onChange={handleChange}
              aria-label="investment Tab"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="All Items" value="1" sx={{textTransform:'capitalize'}} />
            </TabList>
          </Box>
          <TabPanel value="1">
            {
              <DataGrid
                rows={investments}
                columns={columns}
                rowCount={rowCount}
                paginationModel={paginationModel}
                PaginationModelChange={handelPaginationModel}
                filterModel={filterModel}
                loading={loading}
                filterModelChange={(model: GridFilterModel) =>
                  handelFilterModelChange(model)
                }
                customToolbar={CustomToolbar}
              />
            }
          </TabPanel>
        </TabContext>
      </Box>
    </Container>
  );
};

export default InvestmentsDashboard;
